import {
  CalendarIcon,
  CubeIcon,
  DocumentTextIcon,
  IdentificationIcon,
  InformationCircleIcon,
  LightBulbIcon,
  NewspaperIcon,
  PencilSquareIcon,
  VideoCameraIcon
} from '@heroicons/react/24/outline'
import {
  CalendarIcon as CalendarIconSolid,
  CubeIcon as CubeIconSolid,
  DocumentTextIcon as DocumentTextIconSolid,
  IdentificationIcon as IdentificationIconSolid,
  InformationCircleIcon as InformationCircleIconSolid,
  LightBulbIcon as LightBulbIconSolid,
  NewspaperIcon as NewspaperIconSolid,
  PencilSquareIcon as PencilSquareIconSolid,
  VideoCameraIcon as VideoCameraIconSolid
} from '@heroicons/react/24/solid'
import clsx from 'clsx'
import { FC, ReactNode } from 'react'

import { AuthService } from '@/api/models/auth'
import { usePermissions } from '@/api/models/auth/hooks/usePermissions'
import { ProjectSource } from '@/api/models/auth/lib/auth.types'
import { useFinanceBalance } from '@/api/models/finance'
import { usePersonalAccounts } from '@/api/models/finance/hooks/usePersonalAccounts'
import { useSelectedProject } from '@/api/models/main/hooks/useSelectedProject'
import { useProjectFeatures } from '@/api/models/users/hooks/useProjectFeatures'
import { ScreenSize } from '@/app/constants'
import { YandexMetrikaEvent } from '@/app/constants/metrics'
import { useCurrentBreakpoint } from '@/app/hooks/useCurrentBreakpoint'
import BillsIcon from '@/components/shared/icons/BillsIcon'
import ExitIcon from '@/components/shared/icons/ExitIcon'
import { useYandexMetrika } from '@/components/shared/yandex-metrika/hooks/useYandexMetrika'
import { SidebarNavItem } from '@/components/widgets/sidebar/ui/SidebarNavItem'

import { SidebarItem } from './Sidebar.types'
import styles from './SidebarNav.module.css'

type Props = {
  children?: ReactNode
  className?: string
  isSidebarExpanded: boolean
  toggleSidebar: () => void
}

const SidebarNav: FC<Props> = ({
  className,
  isSidebarExpanded,
  toggleSidebar
}) => {
  const permissions = usePermissions()

  const { breakpoint } = useCurrentBreakpoint()

  const { selectedProject } = useSelectedProject()
  const { reachGoal } = useYandexMetrika()

  const { data: projectFeatures } = useProjectFeatures()

  const isLargeScreen = [ScreenSize.Xxl].includes(breakpoint)
  const isMobileScreen = [ScreenSize.Xxs, ScreenSize.Xs].includes(breakpoint)

  const { data: balance } = useFinanceBalance()
  const { data: personalAccounts } = usePersonalAccounts()

  const getTotalBillsToPayCount = () => {
    if (selectedProject?.src === ProjectSource.BU) {
      return balance?.tickets.filter(ticket => ticket.debtSum !== 0).length
    }
    return personalAccounts?.filter(account => account.hasDebt).length
  }

  const logout = () => {
    reachGoal(YandexMetrikaEvent.MAIN_LOGOUT)
    AuthService.logout()
  }

  const handleSidebarToggleOnNavClick = () => {
    if (isSidebarExpanded && !isLargeScreen) {
      toggleSidebar()
    }
  }

  const backendNavItems: SidebarItem[] = [
    ...(permissions.finances
      ? [
          {
            href: '/invoices',
            icon: <BillsIcon className="h-24 w-24" />,
            activeIcon: <BillsIcon className="h-24 w-24" solid />,
            name: 'Счета',
            showDot: (getTotalBillsToPayCount() || 0) > 0,
            count:
              selectedProject?.src === ProjectSource.BU
                ? Math.floor(balance?.totalSum ?? 0)
                : 0
          }
        ]
      : []),
    ...(permissions.finances && permissions.acts
      ? [
          {
            href: '/acts',
            icon: <DocumentTextIcon />,
            activeIcon: <DocumentTextIconSolid />,
            name: 'Акты'
          }
        ]
      : []),
    ...(permissions.counters
      ? [
          {
            href: '/counters',
            icon: <LightBulbIcon />,
            activeIcon: <LightBulbIconSolid />,
            name: 'Приборы учёта'
          }
        ]
      : []),
    ...(permissions.requests
      ? [
          {
            href: '/passes',
            icon: <IdentificationIcon />,
            activeIcon: <IdentificationIconSolid />,
            name: 'Пропуска'
          },
          {
            href: '/requests',
            icon: <PencilSquareIcon />,
            activeIcon: <PencilSquareIconSolid />,
            name: 'Обращения'
          }
        ]
      : []),
    ...(permissions.cameras
      ? [
          {
            href: '/cameras',
            icon: <VideoCameraIcon />,
            activeIcon: <VideoCameraIconSolid />,
            name: 'Камеры'
          }
        ]
      : []),
    ...(permissions.posts
      ? [
          {
            href: '/feed',
            icon: <NewspaperIcon />,
            activeIcon: <NewspaperIconSolid />,
            name: 'Новости и предложения'
          }
        ]
      : []),
    ...(projectFeatures?.arePublicPlacesAvailable
      ? [
          {
            href: '/publicplaces',
            icon: <CubeIcon />,
            activeIcon: <CubeIconSolid />,
            name: 'Пространства'
          }
        ]
      : []),
    ...(projectFeatures?.areActivitiesAvailable
      ? [
          {
            href: '/activities',
            icon: <CalendarIcon />,
            activeIcon: <CalendarIconSolid />,
            name: 'Афиша мероприятий'
          }
        ]
      : [])
  ]

  const staticNavItems: SidebarItem[] = [
    {
      href: '/useful-information',
      icon: <InformationCircleIcon />,
      activeIcon: <InformationCircleIconSolid />,
      name: 'Полезная информация'
    },
    ...(isMobileScreen
      ? [
          {
            className: '!text-red-70',
            icon: <ExitIcon />,
            name: 'Выйти',
            onClick: logout
          }
        ]
      : [])
  ]

  const onSidebarItemClick = (navItem: SidebarItem) => {
    if (navItem.onClick) {
      navItem.onClick()
      return
    }
    handleSidebarToggleOnNavClick()
  }

  return (
    <nav
      className={clsx(
        'overflow-y-scroll xs:overflow-y-visible',
        styles.scroll,
        styles.preventAnchor,
        className
      )}
    >
      <div className="space-y-12 p-20 xs:p-16">
        {backendNavItems.map(item => (
          <SidebarNavItem
            key={item.href ?? item.name}
            isSidebarExpanded={isSidebarExpanded}
            onClick={() => onSidebarItemClick(item)}
            {...item}
          />
        ))}

        <div className="h-1 w-full bg-grey-80" />

        {staticNavItems.map(item => (
          <SidebarNavItem
            key={item.href ?? item.name}
            isSidebarExpanded={isSidebarExpanded}
            onClick={() => onSidebarItemClick(item)}
            {...item}
          />
        ))}
      </div>
    </nav>
  )
}

export { SidebarNav }
